import { formatCodeableConcept } from '@medplum/core';
import type { DocumentReference as FhirDocumentReference } from '@medplum/fhirtypes';
import { cn } from '@liza/ui/utils';
import { AlertCircleIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { DocumentReference } from './DocumentReference';

export type EncounterDocumentsSectionProps = {
  documents: FhirDocumentReference[] | undefined;
  isLoading: boolean | undefined;
  /**
   * Show a placeholder "Progress note" tab in an error state when the generation job failed, so the
   * failure is surfaced (in the slot the note would have filled) rather than silently disappearing.
   * In-progress generation is communicated by the caller separately (the encounter header banner).
   */
  progressNoteFailed?: boolean;
  /** Show the "Documents" heading above the tabs. Defaults to true. */
  showHeading?: boolean;
  /**
   * Grow the section (and the active document) to fill the parent's height rather than sizing to a
   * fixed iframe height. Requires an ancestor chain with a definite height. Used by the encounter
   * note drawer, where the document should occupy the whole panel.
   */
  fill?: boolean;
};

/** Stable tab key for a document — its id when present, otherwise its position. */
const tabValue = (doc: FhirDocumentReference, idx: number): string => doc.id ?? `document-${idx}`;

const FAILED_PROGRESS_NOTE_TAB = 'failed-progress-note';

export const EncounterDocumentsSection = ({
  documents,
  isLoading,
  progressNoteFailed = false,
  showHeading = true,
  fill = false,
}: EncounterDocumentsSectionProps): React.ReactElement | null => {
  const { t } = useTranslation('records');

  // One tab per document, headed by the document reference's type (e.g. "Progress note",
  // "Visit transcription"). Nothing to show if empty.
  const docs = useMemo(() => documents ?? [], [documents]);
  const firstDoc = docs[0];

  // Selectable values: documents first, then the optional failed-note placeholder. We control the
  // Tabs so selection falls back to the first tab if the active value disappears — an uncontrolled
  // Tabs would keep pointing at the gone value and show no active tab. Persisting the derived value
  // keeps passive viewers on the transcript they were reading.
  const tabValues = useMemo(() => {
    const values = docs.map((doc, idx) => tabValue(doc, idx));
    if (progressNoteFailed) values.push(FAILED_PROGRESS_NOTE_TAB);
    return values;
  }, [docs, progressNoteFailed]);

  // Persist the derived value so a passive viewer stays on the tab they were reading rather than
  // being pulled to a newly-arrived leftmost tab (the progress note). A `useState` initializer
  // can't stand in for this: the section mounts while documents are still loading, so the initial
  // `tabValues[0]` is empty and would never become sticky.
  const [selected, setSelected] = useState<string>();
  const activeValue = selected && tabValues.includes(selected) ? selected : tabValues[0];
  useEffect(() => {
    if (activeValue && activeValue !== selected) setSelected(activeValue);
  }, [activeValue, selected]);

  const heading = showHeading ? (
    <h2 className="mb-1 text-xs font-semibold text-muted-foreground">{t('encounterDocuments')}</h2>
  ) : null;

  if (isLoading) {
    return (
      <div className={cn(fill && 'flex h-full min-h-0 flex-col')}>
        {heading}
        <Skeleton className={cn('w-full', fill ? 'min-h-0 flex-1' : 'h-[600px]')} />
      </div>
    );
  }

  if (!firstDoc && !progressNoteFailed) return null;

  return (
    <div className={cn(fill && 'flex h-full min-h-0 flex-col')}>
      {heading}
      <Tabs value={activeValue} onValueChange={setSelected} className={cn('gap-3', fill && 'min-h-0 flex-1')}>
        <TabsList className="w-fit">
          {/* On failure the placeholder leads, occupying the slot the progress note would have
              filled; the transcript stays selected so it remains on its own tab. */}
          {progressNoteFailed && (
            <TabsTrigger
              value={FAILED_PROGRESS_NOTE_TAB}
              data-testid="encounter-document-tab-failed"
              className="gap-1.5"
            >
              {t('progressNote')}
              <AlertCircleIcon className="size-3 text-destructive" />
            </TabsTrigger>
          )}
          {docs.map((doc, idx) => (
            <TabsTrigger
              key={tabValue(doc, idx)}
              value={tabValue(doc, idx)}
              data-testid={`encounter-document-tab-${idx}`}
            >
              {formatCodeableConcept(doc.type) || t('document')}
            </TabsTrigger>
          ))}
        </TabsList>
        {progressNoteFailed && (
          <TabsContent value={FAILED_PROGRESS_NOTE_TAB} className="mt-0" data-testid="encounter-document-panel-failed">
            <p className="flex items-center gap-2 text-sm text-destructive" data-testid="progress-note-error">
              <AlertCircleIcon className="size-4" />
              {t('generateProgressNoteFailed')}
            </p>
          </TabsContent>
        )}
        {docs.map((doc, idx) => (
          <TabsContent
            key={tabValue(doc, idx)}
            value={tabValue(doc, idx)}
            className={cn('mt-0', fill && 'min-h-0 overflow-y-auto')}
            data-testid={`encounter-document-panel-${idx}`}
          >
            <DocumentReference documentReference={doc} fill={fill} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};
